import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { isReviewSubmission } from 'features/libra/lifecycle/lifecycleStageHelpers'
import {
  getDefaultStage,
  type LifecycleConfig,
  LifecycleTriggerType,
} from 'features/libra/lifecycle/lifecycleTypes'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { resolveSignoffRequirements } from 'features/libra/signoff/signoffRequirements'
import type {
  SignoffConfig,
  SignoffsField,
} from 'features/libra/signoff/signoffTypes'
import type { PdfTableData } from 'features/reporting/pdfGeneration/components/shared/PdfTable'

import type {
  NiiReportSection,
  NiiReviewMilestone,
  NiiSignoffEntry,
  NiiTicketLifecycle,
} from './niiReportContentTypes'

dayjs.extend(utc)

export const NII_REVIEW_SECTION_ID = 'review-approval'
export const NII_REVIEW_SECTION_TITLE = 'Review & Approval'

const APPROVED_STAGE_ID = 'approved'

/** A milestone or sign-off that hasn't happened yet. */
const PENDING = 'Pending'
/** A milestone that happened before it was recorded on tickets. */
const NOT_RECORDED = 'Not recorded'
/** A sign-off slot whose `requiredWhen` doesn't hold for this ticket. */
const NOT_REQUIRED = 'Not required'
/** The At cell of anything unsigned or unrecorded, in both tables. */
const NO_TIMESTAMP = '—'

export const NII_APPROVED_BEFORE_SIGNOFF_NOTE =
  'Approved before sign-off was required; no individual sign-offs were recorded.'

/**
 * What every export records per slot. The PDF prints these as table columns
 * and the spreadsheet as `<slot title> – <label>` columns, so both formats
 * describe a sign-off identically.
 */
export const NII_SIGNOFF_FIELD_LABELS = ['Signed By', 'Signed At'] as const

/**
 * The closing section: the sign-off slots, then who submitted and approved
 * the ticket, each in its own table. Undefined when the workflow configures no
 * sign-off and its lifecycle has neither a review submission nor an approved
 * stage. Unsigned slots are kept: the export is the record of the review, so an outstanding
 * sign-off must be visible rather than silently absent.
 */
export function buildNiiReviewSection(
  config: SignoffConfig | undefined,
  signoffs: SignoffsField,
  fieldValues: FieldValues,
  lifecycle: NiiTicketLifecycle | undefined
): NiiReportSection | undefined {
  const reviewMilestones = lifecycle
    ? [resolveSubmission(lifecycle), resolveApproval(lifecycle)].filter(
        (milestone): milestone is NiiReviewMilestone => !!milestone
      )
    : []
  const milestonesOrUndefined =
    reviewMilestones.length > 0 ? reviewMilestones : undefined

  if (!config) {
    if (!milestonesOrUndefined) return undefined
    return {
      stepId: NII_REVIEW_SECTION_ID,
      title: NII_REVIEW_SECTION_TITLE,
      reviewMilestones: milestonesOrUndefined,
    }
  }

  const requirements = resolveSignoffRequirements(
    config.signoffs,
    signoffs,
    fieldValues
  )
  const signoff = requirements.map(
    ({ slot, record, required }): NiiSignoffEntry => ({
      slotId: slot.id,
      title: slot.title,
      signedBy: record?.userName,
      signedAt: record?.signedAt,
      missingText: required ? PENDING : NOT_REQUIRED,
    })
  )

  // Any signature, even one since reset, means sign-off was in use for this
  // ticket and the table is the whole story.
  const neverSigned = requirements.every(
    ({ record, previous }) => !record && !previous
  )

  return {
    stepId: NII_REVIEW_SECTION_ID,
    title: NII_REVIEW_SECTION_TITLE,
    reviewMilestones: milestonesOrUndefined,
    signoff,
    approvedBeforeSignoffRequired:
      neverSigned &&
      !!lifecycle?.config &&
      isSignoffGatedTerminalStage(lifecycle.config, lifecycle.ticketStatus),
  }
}

/**
 * Undefined unless the workflow has an `in_progress → pending_review` move.
 * A ticket still at or before `in_progress` hasn't been submitted; one past it
 * with nothing recorded was submitted before submissions were recorded.
 */
function resolveSubmission({
  config,
  ticketStatus,
  submittedForReviewBy,
  submittedForReviewAt,
}: NiiTicketLifecycle): NiiReviewMilestone | undefined {
  if (!config?.transitions.some(isReviewSubmission)) return undefined
  const notYetSubmitted =
    !ticketStatus ||
    ticketStatus === 'in_progress' ||
    ticketStatus === getDefaultStage(config)?.id
  return {
    label: 'Submitted for Review',
    by: submittedForReviewBy ?? undefined,
    at: submittedForReviewAt ?? undefined,
    missingText: notYetSubmitted ? PENDING : NOT_RECORDED,
  }
}

/**
 * Undefined unless the workflow has an `approved` terminal stage. The current
 * stage is checked rather than `terminalTransitionBy` alone, because leaving a
 * terminal stage does not clear that field — so a ticket sent back out of
 * approval reads "Pending" despite still carrying the old approver.
 */
function resolveApproval({
  config,
  ticketStatus,
  terminalTransitionBy,
  terminalTransitionAt,
}: NiiTicketLifecycle): NiiReviewMilestone | undefined {
  const hasApprovedStage = config?.stages.some(
    ({ id, terminal }) => id === APPROVED_STAGE_ID && terminal
  )
  if (!hasApprovedStage) return undefined
  if (ticketStatus !== APPROVED_STAGE_ID) {
    return { label: 'Approved', missingText: PENDING }
  }
  return {
    label: 'Approved',
    by: terminalTransitionBy ?? undefined,
    at: terminalTransitionAt ?? undefined,
    missingText: NOT_RECORDED,
  }
}

function isSignoffGatedTerminalStage(
  config: LifecycleConfig,
  stageId: string
): boolean {
  const stage = config.stages.find(({ id }) => id === stageId)
  return (
    !!stage?.terminal &&
    config.transitions.some(
      ({ to, trigger }) =>
        to === stageId &&
        trigger.type === LifecycleTriggerType.ACTION &&
        trigger.requireSignoffs === true
    )
  )
}

/**
 * The milestone's `[by, at]`, shared by the PDF table and the spreadsheet's
 * "<label> By" / "<label> At" columns. With nothing recorded, the By cell
 * says why so the row is never ambiguously blank.
 */
export function niiReviewMilestoneValues(
  milestone: NiiReviewMilestone
): readonly [by: string, at: string] {
  return [
    milestone.by ?? milestone.missingText,
    milestone.at ? formatNiiSignoffTimestamp(milestone.at) : NO_TIMESTAMP,
  ]
}

export function buildNiiReviewMilestoneTable(
  milestones: ReadonlyArray<NiiReviewMilestone>
): PdfTableData {
  return {
    headers: ['Stage', 'By', 'At'],
    rows: milestones.map((milestone) => ({
      cells: [milestone.label, ...niiReviewMilestoneValues(milestone)],
    })),
    columnWidths: [0.4, 0.3, 0.3],
  }
}

/**
 * UTC so a report reads the same wherever it is opened or exported; the
 * reporting e-signature audit trail records UTC for the same reason.
 */
export function formatNiiSignoffTimestamp(iso: string): string {
  const parsed = dayjs.utc(iso)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm [UTC]') : iso
}

/**
 * One slot's values, aligned with `NII_SIGNOFF_FIELD_LABELS`. An unsigned
 * slot reads like an unrecorded milestone, so both tables look the same.
 */
export function niiSignoffValues(
  entry: NiiSignoffEntry
): readonly [signedBy: string, signedAt: string] {
  return [
    entry.signedBy ?? entry.missingText,
    entry.signedAt ? formatNiiSignoffTimestamp(entry.signedAt) : NO_TIMESTAMP,
  ]
}

export function buildNiiSignoffTable(
  signoff: ReadonlyArray<NiiSignoffEntry>
): PdfTableData {
  return {
    headers: ['Sign-off', ...NII_SIGNOFF_FIELD_LABELS],
    rows: signoff.map((entry) => ({
      cells: [entry.title, ...niiSignoffValues(entry)],
    })),
    columnWidths: [0.4, 0.3, 0.3],
  }
}
